'use client'

import React, { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import { isHsrpLandingWidget } from '@helpers/vasLandingPageHelper'
import {
  MOBILE_NUMBER_LOCK,
  PHONE_NUMBER_PREFIX,
  ENTER_VALID_MOBILE_NUMBER,
  ENTER_MOBILE_NUMBER,
  BUTTON_CTA,
  ENTER_INFO_MAPPING,
  DESCRIPTION_MAPPING,
  PHONE_NUMBER_INFO,
  CONTINUE_WITH_NUMBER_CTA,
  CHANGE_NUMBER_CTA,
  CONFIRM_MOBILE_NUMBER_HEADING,
  CONFIRM_MOBILE_NUMBER_SUBHEADING,
  MOBILE_NUMBER_ACTION_TYPE,
  MobileNumberActionType,
} from './constants'
import { validateMobile } from './utils'
import styles from './mobileNumberInputModal.module.scss'

interface MobileNumberModalProps {
  isMobile: boolean
  onSubmit?: (mobileNumber: string, actionType?: MobileNumberActionType) => void
  showMobileNumberModal?: boolean
  setShowMobileNumberModal: Function
  widgetType: string
  mobileNumber?: string
  handleSegmentEvent?: (eventName: string, mobileNumber?: string, actionType?: MobileNumberActionType) => void
}

const MobileNumberModal: React.FC<MobileNumberModalProps> = ({
  onSubmit,
  isMobile,
  showMobileNumberModal,
  setShowMobileNumberModal,
  widgetType,
  mobileNumber,
  handleSegmentEvent,
}) => {
  const hasPrefilledNumber = Boolean(mobileNumber && validateMobile(mobileNumber))
  const [mobileNo, setMobileNo] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [isEditingNumber, setIsEditingNumber] = useState<boolean>(!hasPrefilledNumber)
  const modalRef = useRef<HTMLDivElement>(null)
  const isHsrpPage = isHsrpLandingWidget(widgetType)
  const showConfirmVariant = hasPrefilledNumber && !isEditingNumber

  useEffect(() => {
    if (showMobileNumberModal) {
      document.documentElement.style.overflowY = 'hidden'
      setIsEditingNumber(!hasPrefilledNumber)
      setMobileNo('')
      setError('')
    }
  }, [showMobileNumberModal, hasPrefilledNumber])

  const onCloseModal = () => {
    setShowMobileNumberModal(false)
    document.documentElement.style.overflowY = 'scroll'
  }

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onCloseModal()
    }
  }

  const getContinueActionType = (): MobileNumberActionType => {
    if (showConfirmVariant) {
      return MOBILE_NUMBER_ACTION_TYPE.CONTINUE_WITH_THIS_NUMBER
    }
    if (hasPrefilledNumber) {
      return MOBILE_NUMBER_ACTION_TYPE.AFTER_CHANGING_NUMBER
    }
    return MOBILE_NUMBER_ACTION_TYPE.DEFAULT
  }

  const handleContinue = () => {
    const actionType = getContinueActionType()
    if (showConfirmVariant) {
      if (mobileNumber) {
        onSubmit && onSubmit(mobileNumber, actionType)
      }
      return
    }

    if (!validateMobile(mobileNo)) {
      setError(ENTER_VALID_MOBILE_NUMBER)
      return
    }
    onSubmit && onSubmit(mobileNo, actionType)
  }

  const handleChangeNumber = () => {
    setIsEditingNumber(true)
    setMobileNo('')
    setError('')
    handleSegmentEvent?.('Change Phone Number', mobileNumber || '', MOBILE_NUMBER_ACTION_TYPE.CHANGE_NUMBER)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/[^\d]/g, '').slice(0, 10)
    setMobileNo(inputValue)
    if (error) setError('')
  }

  return (
    <div>
      <div className={isMobile ? styles.overlay : styles.desktopOverlay} onClick={handleOverlayClick}>
        <div className={isMobile ? styles.modal : styles.desktopModal} ref={modalRef}>
          <button type="button" className={styles.closeButton} onClick={onCloseModal}>
            &times;
          </button>
          <div className={isMobile ? styles.modalContent : styles.modalDesktopContent}>
            <div className={isMobile ? styles.headerContainer : styles.desktopHeaderContainer}>
              <h1 className={`${styles.header} ${styles.heading}`}>
                {showConfirmVariant
                  ? CONFIRM_MOBILE_NUMBER_HEADING
                  : ENTER_INFO_MAPPING[widgetType] || ENTER_INFO_MAPPING.Default}
              </h1>
              {showConfirmVariant ? (
                <h2 className={`${styles.header} ${styles.subheading}`}>
                  {CONFIRM_MOBILE_NUMBER_SUBHEADING}
                </h2>
              ) : (
                !isHsrpPage && (
                  <h2 className={`${styles.header} ${styles.subheading}`}>
                    {DESCRIPTION_MAPPING[widgetType] || DESCRIPTION_MAPPING.Default}
                  </h2>
                )
              )}
            </div>
            {showConfirmVariant ? (
              <div className={styles.prefilledNumber}>{`${PHONE_NUMBER_PREFIX} ${mobileNumber}`}</div>
            ) : (
              <div>
                <div className={error ? styles.errorInput : styles.inputContainer}>
                  <span>{PHONE_NUMBER_PREFIX}</span>
                  <input
                    id="mobileNumber"
                    type="tel"
                    value={mobileNo}
                    onChange={handleInputChange}
                    placeholder={ENTER_MOBILE_NUMBER}
                    inputMode="numeric"
                    className={styles.input}
                  />
                </div>
                {error && <p className={styles.error}>{ENTER_VALID_MOBILE_NUMBER}</p>}
              </div>
            )}
            <div className={isMobile ? styles.actionSection : styles.desktopActionSection}>
              <button type="button" onClick={handleContinue} className={styles.continueButton}>
                {showConfirmVariant ? CONTINUE_WITH_NUMBER_CTA : BUTTON_CTA}
              </button>
              {showConfirmVariant && (
                <button type="button" onClick={handleChangeNumber} className={styles.changeNumberButton}>
                  {CHANGE_NUMBER_CTA}
                </button>
              )}
              <div className={styles.terms}>
                <Image width={20} height={20} src={MOBILE_NUMBER_LOCK} alt="Loading.." loading="lazy" />
                <p>{PHONE_NUMBER_INFO[widgetType] || PHONE_NUMBER_INFO.Default}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MobileNumberModal
